import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, Layers, Search } from "lucide-react";
import { MAIN_URL } from "../../../Configurations/Urls";
import ListPersonAvatar from "../../../Components/Table/ListPersonAvatar";
import "./BatchModulesGroupedView.css";

function moduleName(row) {
  const batchModule = row?.batch_module || row?.batchModule;
  return (
    batchModule?.program_module?.module_name ||
    batchModule?.programModule?.module_name ||
    "Module"
  );
}

function sequenceNo(row) {
  const batchModule = row?.batch_module || row?.batchModule;
  const value = batchModule?.sequence_no;
  return value == null || value === "" ? null : Number(value);
}

function trainerRecord(row) {
  const batchTrainer = row?.batch_trainer || row?.batchTrainer;
  return batchTrainer?.trainer || null;
}

function trainerName(row) {
  const trainer = trainerRecord(row);
  return (
    String(trainer?.applicationuser?.[0]?.full_name || "").trim() ||
    String(trainer?.person?.full_name || trainer?.Person?.full_name || "").trim() ||
    [trainer?.person?.first_name, trainer?.person?.last_name].filter(Boolean).join(" ") ||
    "Trainer"
  );
}

function trainerPhoto(row) {
  const trainer = trainerRecord(row);
  return (
    trainer?.person?.profile_image_url ||
    trainer?.Person?.profile_image_url ||
    trainer?.organization_person?.person?.profile_image_url ||
    trainer?.organizationPerson?.person?.profile_image_url ||
    trainer?.profile_image_url ||
    ""
  );
}

function batchTrainerOf(row) {
  return row?.batch_trainer || row?.batchTrainer || null;
}

function batchOf(row) {
  return batchTrainerOf(row)?.batch || null;
}

function trainerKey(row) {
  const batchTrainer = batchTrainerOf(row);
  return String(
    batchTrainer?.organization_ems_batch_trainer_id ??
      batchTrainer?.trainer_employee_id ??
      trainerName(row)
  );
}

function trainerRole(row) {
  return batchTrainerOf(row)?.trainer_role || "";
}

function compareSequence(a, b) {
  const seqA = sequenceNo(a);
  const seqB = sequenceNo(b);
  if (seqA == null && seqB == null) return 0;
  if (seqA == null) return 1;
  if (seqB == null) return -1;
  return seqA - seqB;
}

function groupByBatch(rows) {
  const groups = new Map();
  rows.forEach((row) => {
    const batch = batchOf(row);
    const batchId = batch?.organization_ems_batch_id ?? "none";
    if (!groups.has(batchId)) {
      groups.set(batchId, {
        id: batchId,
        label: batch?.batch_code || batch?.batch_name || "Batch",
        program:
          batch?.training_program?.training_program_name ||
          batch?.trainingProgram?.training_program_name ||
          "",
        trainers: new Map(),
      });
    }
    const trainers = groups.get(batchId).trainers;
    const key = trainerKey(row);
    if (!trainers.has(key)) {
      trainers.set(key, {
        id: key,
        name: trainerName(row),
        photo: trainerPhoto(row),
        role: trainerRole(row),
        modules: [],
      });
    }
    trainers.get(key).modules.push(row);
  });

  return Array.from(groups.values())
    .map((group) => {
      const trainers = Array.from(group.trainers.values())
        .map((trainer) => ({
          ...trainer,
          modules: [...trainer.modules].sort(compareSequence),
        }))
        .sort((a, b) => {
          const seqA = sequenceNo(a.modules[0]);
          const seqB = sequenceNo(b.modules[0]);
          if (seqA == null && seqB == null) return a.name.localeCompare(b.name);
          if (seqA == null) return 1;
          if (seqB == null) return -1;
          if (seqA !== seqB) return seqA - seqB;
          return a.name.localeCompare(b.name);
        });
      const modules = trainers.flatMap((trainer) => trainer.modules);
      return { ...group, trainers, modules };
    })
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
}

export default function TrainerModulesGroupedView({ organizationId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!organizationId) {
      setRows([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${MAIN_URL}/api/ems/batch-trainer-modules`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          params: { organization_id: organizationId },
        });
        const data = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError(err?.response?.data?.message || "Could not load trainer modules.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const grouped = groupByBatch(rows);
    if (!needle) return grouped;
    return grouped
      .map((group) => {
        const batchHit =
          String(group.label).toLowerCase().includes(needle) ||
          String(group.program).toLowerCase().includes(needle);
        const trainers = group.trainers
          .map((trainer) => {
            const trainerHit = trainer.name.toLowerCase().includes(needle);
            if (batchHit || trainerHit) return trainer;
            const modules = trainer.modules.filter((row) =>
              moduleName(row).toLowerCase().includes(needle)
            );
            return { ...trainer, modules };
          })
          .filter((trainer) => trainer.modules.length > 0);
        const modules = trainers.flatMap((trainer) => trainer.modules);
        return { ...group, trainers, modules };
      })
      .filter((group) => group.modules.length > 0);
  }, [rows, query]);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section className="bm-board">
      <div className="ems-toolbar">
        <label className="ems-search bm-search">
          <Search size={16} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search batch, module, or trainer"
          />
        </label>
        <span className="bm-count">
          {groups.length} batch{groups.length === 1 ? "" : "es"}
        </span>
      </div>

      {loading ? <div className="ems-card ems-empty">Loading trainer modules…</div> : null}
      {!loading && error ? <div className="ems-card ems-empty">{error}</div> : null}
      {!loading && !error && groups.length === 0 ? (
        <div className="ems-card ems-empty">
          <div className="ems-empty-icon">
            <Layers size={22} />
          </div>
          No trainer modules found.
        </div>
      ) : null}

      {!loading &&
        groups.map((group) => {
          const isClosed = !expanded[group.id];
          const active = group.modules.filter((row) => row.status === "Active").length;
          const progress = group.modules.length
            ? Math.round((active / group.modules.length) * 100)
            : 0;
          return (
            <article key={group.id} className="ems-card bm-batch">
              <button type="button" className="bm-batch-head" onClick={() => toggle(group.id)}>
                <span className="ems-stat-icon">
                  <Layers size={18} />
                </span>
                <div className="bm-batch-copy">
                  <div className="bm-batch-title">
                    <span className="bm-batch-name">{group.label}</span>
                    <span className="ems-badge neutral">
                      {group.trainers.length} trainer{group.trainers.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  {group.program ? <div className="bm-batch-program">{group.program}</div> : null}
                  <div className="bm-progress" aria-hidden="true">
                    <span style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <div className="bm-batch-meta">
                  <strong>
                    {active}/{group.modules.length}
                  </strong>
                  <span>active</span>
                </div>
                <ChevronDown size={18} className={isClosed ? "bm-chevron is-closed" : "bm-chevron"} />
              </button>

              {!isClosed ? (
                <div className="bm-trainer-list">
                  {group.trainers.map((trainer) => (
                    <section key={trainer.id} className="bm-trainer-block">
                      <div className="bm-trainer-head">
                        <ListPersonAvatar
                          row={{ profile_image_url: trainer.photo }}
                          size={36}
                          previewAlt={trainer.name}
                        />
                        <div className="bm-trainer-copy">
                          <div className="bm-trainer-name">{trainer.name}</div>
                          <div className="bm-batch-program">
                            {[trainer.role, `${trainer.modules.length} module${trainer.modules.length === 1 ? "" : "s"}`]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        </div>
                      </div>
                      <ol className="bm-modules">
                        {trainer.modules.map((row, index) => {
                          const status = row.status || "Active";
                          const isLast = index === trainer.modules.length - 1;
                          const seq = sequenceNo(row);
                          return (
                            <li
                              key={row.organization_ems_batch_trainer_module_id}
                              className={isLast ? "bm-module is-trainer is-last" : "bm-module is-trainer"}
                            >
                              <span className={`bm-seq${status === "Active" ? " is-live" : ""}`}>
                                {seq == null || Number.isNaN(seq) ? "—" : seq}
                              </span>
                              <div className="bm-module-name">{moduleName(row)}</div>
                              <span className={status === "Active" ? "ems-badge green" : "ems-badge bm-badge-muted"}>
                                {status}
                              </span>
                            </li>
                          );
                        })}
                      </ol>
                    </section>
                  ))}
                </div>
              ) : null}
            </article>
          );
        })}
    </section>
  );
}
